import { InfoIcon, WarningCircleIcon } from '@phosphor-icons/react/dist/ssr';

import type { GroupSubscriptionData } from '../groupSubscriptionTypes';
import { GroupCardHeader } from './GroupCardHeader';

type PrePaymentGroupCardProps = {
  group: GroupSubscriptionData;
  onRetryFailedPayment?: () => void;
  translations?: Record<string, string>;
};

const DEFAULT_COPY = {
  setting_up: {
    body: "We're processing your payment. We'll email you as soon as it's confirmed, and you'll be able to start inviting members then.",
  },
  payment_failed: {
    title: 'Payment failed',
    body: "We couldn't process your payment, so your group subscription wasn't created. Try again to continue.",
    retry: 'Retry payment',
  },
} as const;

export function PrePaymentGroupCard({ group, onRetryFailedPayment, translations }: PrePaymentGroupCardProps) {
  const isFailed = group.status === 'payment_failed';

  // The first invoice hasn't posted yet, so total_amount_cents is null —
  // estimate from per-seat × max_seats so the header still shows a Total line.
  const estimatedTotalCents = group.total_amount_cents ?? group.per_seat_amount_cents * group.max_seats;

  return (
    <div className="flex flex-col gap-4 rounded-md border border-gray-200 bg-white p-4">
      <GroupCardHeader
        group={group}
        hideToggle
        totalCents={estimatedTotalCents}
        translations={translations}
        rightSlot={null}
      />

      {isFailed ? (
        <PaymentFailedBanner onRetryFailedPayment={onRetryFailedPayment} translations={translations} />
      ) : (
        <SettingUpBanner translations={translations} />
      )}
    </div>
  );
}

function SettingUpBanner({ translations }: { translations?: Record<string, string> }) {
  const body = translations?.['group_subscriptions.card.setting_up.body'] ?? DEFAULT_COPY.setting_up.body;
  return (
    <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4">
      <InfoIcon weight="fill" width={20} height={20} className="mt-0.5 shrink-0 text-amber-500" />
      <p className="text-sm font-normal leading-5 text-amber-900">{body}</p>
    </div>
  );
}

function PaymentFailedBanner({
  onRetryFailedPayment,
  translations,
}: {
  onRetryFailedPayment?: () => void;
  translations?: Record<string, string>;
}) {
  const title = translations?.['group_subscriptions.card.payment_failed.title'] ?? DEFAULT_COPY.payment_failed.title;
  const body = translations?.['group_subscriptions.card.payment_failed.body'] ?? DEFAULT_COPY.payment_failed.body;
  const retry = translations?.['group_subscriptions.card.payment_failed.retry'] ?? DEFAULT_COPY.payment_failed.retry;

  return (
    <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4">
      <WarningCircleIcon weight="fill" width={20} height={20} className="mt-0.5 shrink-0 text-red-500" />
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium leading-5 text-red-900">{title}</p>
        <p className="text-sm font-normal leading-5 text-red-900">{body}</p>
        {onRetryFailedPayment && (
          <button
            type="button"
            onClick={onRetryFailedPayment}
            className="w-fit text-sm font-medium text-red-900 underline hover:no-underline"
          >
            {retry}
          </button>
        )}
      </div>
    </div>
  );
}
